package fr.manyverse.app.presentation.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.util.toDisplayNameFromFileName
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_name
import manyverse.shared.generated.resources.character_name_hint
import manyverse.shared.generated.resources.character_story
import manyverse.shared.generated.resources.character_story_hint
import manyverse.shared.generated.resources.field_required
import manyverse.shared.generated.resources.image_optional
import manyverse.shared.generated.resources.save
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Editable character form (image, name, story) with save/cancel actions.
 *
 * Extracted from `CharacterEditScreen` so the same form can be reused both as a full
 * screen and as the detail pane of a list-detail layout on large screens.
 *
 * The form owns its own field state, keyed on [character], and drives
 * `CharacterViewModel.updateCharacter`. [onSaved] fires after a successful save.
 *
 * @param onCancel When non-null, a cancel button is shown. Null hides it (e.g. a pane
 *        where there is nothing to navigate back from).
 */
@Composable
fun CharacterEditForm(
    userId: String,
    character: Character,
    modifier: Modifier = Modifier,
    viewModel: CharacterViewModel = koinViewModel(),
    onSaved: () -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val updateState by viewModel.updateCharacterState.collectAsStateWithLifecycle()

    var name by remember(character) { mutableStateOf(character.name) }
    var story by remember(character) { mutableStateOf(character.story) }
    val imageSelection = rememberImageSelection { image ->
        if (name.isBlank()) name = image.fileName.toDisplayNameFromFileName()
    }
    val currentImageUrl = imageSelection.displayUrl(character.signedImageUrl)

    LaunchedEffect(updateState) {
        if (updateState is AsyncUiState.Success) {
            viewModel.resetUpdateCharacterState()
            onSaved()
        }
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .imePadding()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                ImagePickerButton(
                    currentImageUrl = currentImageUrl,
                    selectedImageData = imageSelection.selected,
                    onPickImage = imageSelection::pick,
                    onRemoveImage = imageSelection::clear,
                    enabled = updateState !is AsyncUiState.Loading,
                    modifier = Modifier.testTag("imagePickerButton")
                )
                Text(
                    text = stringResource(Res.string.image_optional),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        ValidatedTextField(
            value = name,
            onValueChange = { name = it },
            label = stringResource(Res.string.character_name),
            placeholder = stringResource(Res.string.character_name_hint),
            helperText = if (name.isBlank()) stringResource(Res.string.field_required) else null,
            enabled = updateState !is AsyncUiState.Loading,
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
            modifier = Modifier.testTag("nameField")
        )

        OutlinedTextField(
            value = story,
            onValueChange = { story = it },
            label = { Text(stringResource(Res.string.character_story)) },
            placeholder = { Text(stringResource(Res.string.character_story_hint)) },
            minLines = 5,
            maxLines = 10,
            enabled = updateState !is AsyncUiState.Loading,
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
            modifier = Modifier
                .fillMaxWidth()
                .testTag("storyField")
        )

        if (updateState is AsyncUiState.Error) {
            Card(
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.errorContainer
                ),
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag("errorCard")
            ) {
                Text(
                    text = (updateState as AsyncUiState.Error).message,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                    modifier = Modifier
                        .padding(16.dp)
                        .testTag("errorMessage")
                )
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (onCancel != null) {
                OutlinedButton(
                    onClick = onCancel,
                    enabled = updateState !is AsyncUiState.Loading,
                    modifier = Modifier
                        .weight(1f)
                        .testTag("cancelButton")
                ) {
                    Text(stringResource(Res.string.cancel))
                }
            }

            Button(
                onClick = {
                    viewModel.updateCharacter(
                        userId = userId,
                        characterId = character.id,
                        characterUniverseId = character.universeId,
                        name = name,
                        story = story,
                        imageData = imageSelection.selected,
                        removeImage = imageSelection.isRemovalRequested,
                        currentImageUrl = character.imageUrl
                    )
                },
                enabled = name.isNotBlank() && updateState !is AsyncUiState.Loading,
                modifier = Modifier
                    .weight(1f)
                    .testTag("saveButton")
            ) {
                if (updateState is AsyncUiState.Loading) {
                    CircularProgressIndicator(
                        modifier = Modifier
                            .size(24.dp)
                            .testTag("saveLoadingIndicator"),
                        color = MaterialTheme.colorScheme.onPrimary,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(stringResource(Res.string.save))
                }
            }
        }
    }
}
